import {
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
  Chip,
  Paper,
  Avatar,
  TextField,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { themeOrange, themeBlue } from "../../utils/colorTheme";
import { MdSend, MdClose, MdRefresh } from "react-icons/md";
import { asyncStatus } from "../../utils/asyncStatus";
import {
  getAdminTickets,
  getAdminTicketById,
  adminReplyToTicket,
  updateTicketStatus,
} from "../../services/SupportTicketService";
import {
  resetReplyStatus,
  clearCurrentTicket,
  setCurrentTicket,
} from "../../store/slices/supportTicketsSlice";
import { format } from "date-fns";

const DashboardSupport = () => {
  const dispatch = useDispatch();
  const [replyText, setReplyText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const {
    tickets,
    tickets_status,
    tickets_stats,
    current_ticket,
    current_ticket_status,
    reply_status,
    update_status_status,
  } = useSelector((state) => state.supportTickets);

  const isLoading = tickets_status === asyncStatus.LOADING;
  const isLoadingTicket = current_ticket_status === asyncStatus.LOADING;
  const isReplying = reply_status === asyncStatus.LOADING;
  const isUpdatingStatus = update_status_status === asyncStatus.LOADING;

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, priorityFilter]);

  useEffect(() => {
    if (reply_status === asyncStatus.SUCCEEDED) {
      setReplyText("");
      dispatch(resetReplyStatus());
    }
  }, [reply_status, dispatch]);

  const fetchTickets = () => {
    const filters = {};
    if (statusFilter !== "all") filters.status = statusFilter;
    if (priorityFilter !== "all") filters.priority = priorityFilter;
    dispatch(getAdminTickets(filters));
  };

  const handleSelectTicket = (ticket) => {
    dispatch(setCurrentTicket(ticket));
    dispatch(getAdminTicketById(ticket.id));
  };

  const handleCloseTicket = () => {
    dispatch(clearCurrentTicket());
  };

  const handleReply = () => {
    if (!replyText.trim() || !current_ticket) return;
    dispatch(adminReplyToTicket({ ticketId: current_ticket.id, message: replyText }));
  };

  const handleStatusChange = (newStatus) => {
    if (!current_ticket) return;
    dispatch(updateTicketStatus({ ticketId: current_ticket.id, status: newStatus }));
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      open: { label: "Open", color: "#2196f3", bg: "#e3f2fd" },
      in_progress: { label: "In Progress", color: themeOrange, bg: "#fff4e6" },
      resolved: { label: "Resolved", color: "#22c55e", bg: "#dcfce7" },
      closed: { label: "Closed", color: "#9e9e9e", bg: "#f5f5f5" },
    };
    const config = statusConfig[status] || statusConfig.open;
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{ backgroundColor: config.bg, color: config.color, fontWeight: 600 }}
      />
    );
  };

  const getPriorityChip = (priority) => {
    const priorityConfig = {
      urgent: { label: "Urgent", color: "#d32f2f" },
      high: { label: "High", color: "#ef4444" },
      medium: { label: "Medium", color: themeOrange },
      low: { label: "Low", color: "#22c55e" },
    };
    const config = priorityConfig[priority] || priorityConfig.low;
    return (
      <Chip
        label={config.label}
        size="small"
        variant="outlined"
        sx={{ borderColor: config.color, color: config.color }}
      />
    );
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy 'at' h:mm a");
    } catch {
      return dateString;
    }
  };

  return (
    <Stack bgcolor={"#FAFAFA"} sx={{ height: "calc(100vh - 100px)" }}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1}
        my={1}
        flexWrap="wrap"
      >
        <Typography className="mainHeading">Support Tickets</Typography>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          {tickets_stats && (
            <>
              <Chip
                label={`${tickets_stats.open || 0} Open`}
                sx={{ backgroundColor: "#e3f2fd", color: "#2196f3" }}
              />
              <Chip
                label={`${tickets_stats.in_progress || 0} In Progress`}
                sx={{ backgroundColor: "#fff4e6", color: themeOrange }}
              />
              <Chip
                label={`${tickets_stats.resolved || 0} Resolved`}
                sx={{ backgroundColor: "#dcfce7", color: "#22c55e" }}
              />
            </>
          )}
          <IconButton onClick={fetchTickets} size="small">
            <MdRefresh />
          </IconButton>
        </Stack>
      </Stack>

      {/* Filters */}
      <Stack direction="row" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            value={priorityFilter}
            label="Priority"
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Divider />

      <Container
        sx={{
          maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" },
          flex: 1,
          overflow: "hidden",
          px: { xs: 0, sm: 2 },
        }}
      >
        <Stack my={2} direction="row" gap={2} sx={{ height: "calc(100% - 20px)" }}>
          {/* Tickets List */}
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: "16px",
              boxShadow: "0px 4px 10px 0px #00000026",
              overflow: "auto",
            }}
          >
            {isLoading ? (
              <Stack alignItems="center" justifyContent="center" py={4}>
                <CircularProgress sx={{ color: themeOrange }} />
              </Stack>
            ) : tickets.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" py={4}>
                <Typography color="text.secondary">No tickets found</Typography>
              </Stack>
            ) : (
              tickets.map((ticket) => (
                <Stack
                  key={ticket.id}
                  p={2}
                  sx={{
                    borderBottom: "1px solid #e5e5e5",
                    cursor: "pointer",
                    backgroundColor:
                      current_ticket?.id === ticket.id ? "#f5f5f5" : "white",
                    "&:hover": { backgroundColor: "#f9f9f9" },
                  }}
                  onClick={() => handleSelectTicket(ticket)}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Stack direction="row" gap={1.5} alignItems="center">
                      <Avatar sx={{ width: 40, height: 40, bgcolor: themeBlue }}>
                        {ticket.user?.first_name?.[0] || ticket.user?.email?.[0] || "U"}
                      </Avatar>
                      <Stack>
                        <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                          {ticket.user?.first_name} {ticket.user?.last_name}
                        </Typography>
                        <Typography sx={{ color: "#666", fontSize: "12px" }}>
                          {ticket.user?.email}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack alignItems="flex-end" gap={0.5}>
                      {getStatusChip(ticket.status)}
                      <Typography sx={{ color: "#999", fontSize: "11px" }}>
                        #{ticket.ticket_number}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Typography sx={{ fontWeight: 500, mt: 1 }}>
                    {ticket.subject}
                  </Typography>
                  <Typography
                    sx={{ color: "#666", fontSize: "13px", mt: 0.5 }}
                    noWrap
                  >
                    {ticket.description}
                  </Typography>
                  <Stack direction="row" gap={1} mt={1} alignItems="center">
                    {getPriorityChip(ticket.priority)}
                    <Chip
                      label={ticket.category}
                      size="small"
                      sx={{ textTransform: "capitalize" }}
                    />
                    {ticket.replies?.length > 0 && (
                      <Chip label={`${ticket.replies.length} replies`} size="small" />
                    )}
                  </Stack>
                </Stack>
              ))
            )}
          </Paper>

          {/* Ticket Detail */}
          {current_ticket && (
            <Paper
              elevation={0}
              sx={{
                flex: 1.5,
                borderRadius: "16px",
                boxShadow: "0px 4px 10px 0px #00000026",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header */}
              <Stack
                p={2}
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ borderBottom: "1px solid #e5e5e5" }}
              >
                <Stack flex={1}>
                  <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
                    {current_ticket.subject}
                  </Typography>
                  <Typography sx={{ color: "#666", fontSize: "13px" }}>
                    #{current_ticket.ticket_number} - {formatDate(current_ticket.created_at)}
                  </Typography>
                  <Stack direction="row" gap={1} mt={1}>
                    {getStatusChip(current_ticket.status)}
                    {getPriorityChip(current_ticket.priority)}
                    <Chip
                      label={current_ticket.category}
                      size="small"
                      sx={{ textTransform: "capitalize" }}
                    />
                  </Stack>
                </Stack>
                <Stack direction="row" gap={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select
                      value={current_ticket.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={isUpdatingStatus}
                      sx={{ fontSize: "13px" }}
                    >
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton onClick={handleCloseTicket}>
                    <MdClose />
                  </IconButton>
                </Stack>
              </Stack>

              {/* Messages */}
              <Stack flex={1} p={2} gap={2} sx={{ overflow: "auto" }}>
                {isLoadingTicket ? (
                  <Stack alignItems="center" py={4}>
                    <CircularProgress sx={{ color: themeOrange }} />
                  </Stack>
                ) : (
                  <>
                    {/* Original Message */}
                    <Stack direction="row" gap={1.5}>
                      <Avatar sx={{ bgcolor: themeBlue }}>
                        {current_ticket.user?.first_name?.[0] || "U"}
                      </Avatar>
                      <Stack
                        sx={{
                          backgroundColor: "#f5f5f5",
                          borderRadius: "12px",
                          p: 2,
                          flex: 1,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ fontWeight: 500 }}>
                            {current_ticket.user?.first_name} {current_ticket.user?.last_name}
                          </Typography>
                          <Typography sx={{ color: "#999", fontSize: "12px" }}>
                            {formatDate(current_ticket.created_at)}
                          </Typography>
                        </Stack>
                        <Typography sx={{ mt: 1 }}>{current_ticket.description}</Typography>
                      </Stack>
                    </Stack>

                    {/* Replies */}
                    {current_ticket.replies?.map((reply, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        gap={1.5}
                        justifyContent={reply.is_admin ? "flex-end" : "flex-start"}
                      >
                        {!reply.is_admin && (
                          <Avatar sx={{ bgcolor: themeBlue }}>
                            {reply.user?.first_name?.[0] || "U"}
                          </Avatar>
                        )}
                        <Stack
                          sx={{
                            backgroundColor: reply.is_admin ? themeBlue : "#f5f5f5",
                            color: reply.is_admin ? "white" : "inherit",
                            borderRadius: "12px",
                            p: 2,
                            maxWidth: "80%",
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" gap={2}>
                            <Typography sx={{ fontWeight: 500 }}>
                              {reply.is_admin ? "Admin" : `${reply.user?.first_name || "User"}`}
                            </Typography>
                            <Typography
                              sx={{
                                color: reply.is_admin ? "rgba(255,255,255,0.7)" : "#999",
                                fontSize: "12px",
                              }}
                            >
                              {formatDate(reply.created_at)}
                            </Typography>
                          </Stack>
                          <Typography sx={{ mt: 1 }}>{reply.message}</Typography>
                        </Stack>
                        {reply.is_admin && (
                          <Avatar sx={{ bgcolor: themeOrange }}>A</Avatar>
                        )}
                      </Stack>
                    ))}
                  </>
                )}
              </Stack>

              {/* Reply Input */}
              {current_ticket.status !== "closed" && (
                <Stack p={2} sx={{ borderTop: "1px solid #e5e5e5" }}>
                  <Stack direction="row" gap={1}>
                    <TextField
                      fullWidth
                      placeholder="Type your reply..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      size="small"
                      multiline
                      maxRows={3}
                      disabled={isReplying}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleReply();
                        }
                      }}
                    />
                    <IconButton
                      onClick={handleReply}
                      disabled={isReplying || !replyText.trim()}
                      sx={{
                        backgroundColor: themeOrange,
                        color: "white",
                        "&:hover": { backgroundColor: "#d14e20" },
                        "&:disabled": { backgroundColor: "#ccc" },
                      }}
                    >
                      {isReplying ? (
                        <CircularProgress size={20} sx={{ color: "white" }} />
                      ) : (
                        <MdSend />
                      )}
                    </IconButton>
                  </Stack>
                </Stack>
              )}

              {current_ticket.status === "closed" && (
                <Box sx={{ p: 2, backgroundColor: "#f5f5f5", textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    This ticket has been closed
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Stack>
      </Container>
    </Stack>
  );
};

export default DashboardSupport;
